import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type Consignment,
  type ConsignmentPayload,
  useCreateConsignmentMutation,
  useUpdateConsignmentMutation,
} from '../../store/api/consignmentApi'
import {
  type LookupItem,
  useGetLookupCertificationsQuery,
  useLazyGetLookupClientsQuery,
  useLazyGetLookupPackagesQuery,
  useLazyGetLookupQualitiesQuery,
} from '../../store/api/saleContractApi'
import DateTextField from '../../components/DateTextField'

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

const readonlySx = {
  '& .MuiInputBase-root': { bgcolor: 'action.hover' },
  '& .MuiInputBase-input': { color: 'text.secondary', cursor: 'default' },
} as const

const toNum = (v: string): number | null => {
  if (!v) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

interface ConsignmentFormModel {
  contractNo: string
  contractDate: string
  clientCode: string
  clientName: string
  quantityKg: string
  packagingCode: string
  packaging: string
  qualityCode: string
  certification: string
  lastFixingDate: string
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): ConsignmentFormModel => ({
  contractNo: '',
  contractDate: today(),
  clientCode: '',
  clientName: '',
  quantityKg: '' as string,
  packagingCode: '',
  packaging: '',
  qualityCode: '',
  certification: '',
  lastFixingDate: '',
})

const consignmentToForm = (c: Consignment): ConsignmentFormModel => ({
  contractNo: c.contractNo,
  contractDate: c.contractDate ?? '',
  clientCode: c.clientCode,
  clientName: c.clientName ?? '',
  quantityKg: c.quantityKg != null ? String(c.quantityKg) : '',
  packagingCode: c.packagingCode ?? '',
  packaging: c.packaging ?? c.packagingCode ?? '',
  qualityCode: c.qualityCode,
  certification: c.certification,
  lastFixingDate: c.lastFixingDate ?? '',
})

const formToPayload = (f: ConsignmentFormModel): ConsignmentPayload => ({
  contractNo: f.contractNo.trim(),
  contractDate: f.contractDate,
  clientCode: f.clientCode.trim(),
  quantityKg: toNum(f.quantityKg) ?? 0,
  packagingCode: f.packagingCode || null,
  packaging: f.packaging || null,
  qualityCode: f.qualityCode.trim(),
  certification: f.certification.trim(),
  lastFixingDate: f.lastFixingDate,
})

interface ConsignmentFormDialogProps {
  open: boolean
  onClose: () => void
  mode: 'new' | 'edit'
  consignment?: Consignment | null
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save consignment.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save consignment.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save consignment.'
}

export default function ConsignmentFormDialog({ open, onClose, mode, consignment }: ConsignmentFormDialogProps) {
  const [form, setForm] = useState<ConsignmentFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [createConsignment, { isLoading: isCreating }] = useCreateConsignmentMutation()
  const [updateConsignment, { isLoading: isUpdating }] = useUpdateConsignmentMutation()

  const [fetchClients, { data: clientOpts, isFetching: clientsLoading }] = useLazyGetLookupClientsQuery()
  const [fetchPackages, { data: packageOpts, isFetching: packagesLoading }] = useLazyGetLookupPackagesQuery()
  const [fetchQualities, { data: qualityOpts, isFetching: qualitiesLoading }] = useLazyGetLookupQualitiesQuery()
  const { data: certifications } = useGetLookupCertificationsQuery(undefined, { skip: !open })

  useEffect(() => {
    if (!open) return
    setValidationError(null)
    setForm(mode === 'edit' && consignment ? consignmentToForm(consignment) : emptyForm())
    fetchClients()
    fetchPackages()
    fetchQualities()
  }, [consignment, fetchClients, fetchPackages, fetchQualities, mode, open])

  const clientItems = useMemo(() => clientOpts?.items ?? [], [clientOpts])
  const packageItems = useMemo(() => packageOpts?.items ?? [], [packageOpts])
  const qualityItems = useMemo(() => qualityOpts?.items ?? [], [qualityOpts])
  const certItems = useMemo(() => certifications?.items ?? [], [certifications])

  const set = useCallback(
    <K extends keyof ConsignmentFormModel>(key: K, value: ConsignmentFormModel[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  )

  const validateLikeAccess = (): string | null => {
    if (!form.contractNo.trim()) return 'Please type Consignment No!'
    if (!form.contractDate) return 'Please type Consignment Date!'
    if (!form.clientCode.trim()) return 'Please choose Client!'
    if (form.quantityKg === '') return 'Please type Quantity!'
    if (!form.certification.trim()) return 'Please choose Certification!'
    if (!form.qualityCode.trim()) return 'Please choose Quality!'
    if (!form.packaging.trim() && !form.packagingCode.trim()) return 'Please choose Package!'
    if (!form.lastFixingDate) return 'Please choose Last fixing date!'
    return null
  }

  const handleSave = async () => {
    const accessError = validateLikeAccess()
    if (accessError) {
      setValidationError(accessError)
      return
    }
    setValidationError(null)
    const body = formToPayload(form)
    try {
      if (mode === 'edit' && consignment) {
        await updateConsignment({ contractNo: consignment.contractNo, body }).unwrap()
      } else {
        await createConsignment(body).unwrap()
      }
      onClose()
    } catch (e) {
      setValidationError(extractApiErrorMessage(e))
    }
  }

  const isBusy = isCreating || isUpdating

  const renderAutocomplete = (
    label: string,
    value: string,
    options: LookupItem[],
    loading: boolean,
    tabIndex: number,
    onInputChange: (q: string) => void,
    onChange: (item: LookupItem | null) => void,
  ) => (
    <Autocomplete
      size="small"
      options={options}
      loading={loading}
      inputValue={value}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      filterOptions={(x) => x}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') onInputChange(v)
      }}
      onChange={(_, v) => onChange(v)}
      renderInput={(params) => (
        <TextField {...params} label={label} inputProps={{ ...params.inputProps, tabIndex }} />
      )}
    />
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          Sale Contract - Consignment
        </Typography>
        <Button
          variant="contained"
          size="small"
          color="success"
          onClick={handleSave}
          disabled={isBusy}
          sx={{ minHeight: 28, py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
        >
          {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ minHeight: 28, py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
        >
          Cancel
        </Button>
      </DialogTitle>
      <Divider />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiInputLabel-shrink': { fontWeight: 600 },
          '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiCheckbox-root': { p: 0.5 },
          '& .MuiSvgIcon-root': { fontSize: '1rem' },
          '& .MuiTypography-body2': { fontSize: '0.8125rem' },
          '& .MuiSelect-select': { fontSize: '0.8125rem' },
        }}
      >
        {validationError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {validationError}
          </Typography>
        )}

        <Box sx={{ maxWidth: 620, mx: 'auto', pb: 1 }}>
          <Grid container spacing={0.9}>
            <Grid item xs={12}>
              <TextField
                label="Consignment  Code"
                size="small"
                fullWidth
                value={form.contractNo}
                onChange={(e) => set('contractNo', e.target.value)}
                InputProps={{ readOnly: mode === 'edit' }}
                sx={mode === 'edit' ? readonlySx : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <DateTextField
                label="Consignment Date"
                size="small"
                fullWidth
                value={form.contractDate}
                onChange={(value) => set('contractDate', value)}
                nativeInputProps={{ tabIndex: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Client Ref',
                form.clientName || form.clientCode,
                clientItems,
                clientsLoading,
                2,
                (q) => {
                  set('clientName', q)
                  fetchClients(q)
                },
                (item) => {
                  set('clientCode', item?.value ?? '')
                  set('clientName', item?.label ?? '')
                },
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Quantity"
                size="small"
                fullWidth
                value={fmtNumStr(form.quantityKg)}
                onChange={(e) => set('quantityKg', e.target.value.replace(/,/g, ''))}
                inputProps={{ tabIndex: 3, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Certification',
                form.certification,
                certItems,
                false,
                4,
                (q) => set('certification', q),
                (item) => set('certification', item?.value ?? ''),
              )}
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Quality',
                form.qualityCode,
                qualityItems,
                qualitiesLoading,
                5,
                (q) => {
                  set('qualityCode', q)
                  fetchQualities(q)
                },
                (item) => set('qualityCode', item?.value ?? ''),
              )}
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Package',
                form.packaging,
                packageItems,
                packagesLoading,
                6,
                (q) => {
                  set('packaging', q)
                  fetchPackages(q)
                },
                (item) => {
                  const packagingCode = typeof item?.meta?.packaging_code === 'string' ? item.meta.packaging_code : item?.value
                  set('packaging', item?.label ?? '')
                  set('packagingCode', packagingCode ?? '')
                },
              )}
            </Grid>
            <Grid item xs={12}>
              <DateTextField
                label="Last fixing date"
                size="small"
                fullWidth
                value={form.lastFixingDate}
                onChange={(value) => set('lastFixingDate', value)}
                nativeInputProps={{ tabIndex: 7 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
